import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { getAPI } from 'services/api/apiAsync';
import { API_ENDPOINTS } from 'utils/constants';
import { logErrors } from 'utils';
import { selectAccessToken } from 'services/session/sessionSelectors';
import type { DashboardSummary } from 'services/dashboard/types';

// Component-local source of truth for the dashboard summary.
// Replaces the Redux dashboard slice — the summary is no longer cached
// across navigation through the store. Single fetch, shared between every
// consumer that mounts inside the provider (currently the sidebar's
// "alerts vs alertsZero" icon and the dashboard alerts card's unread
// count). Consumers that need fresh data (e.g. the dashboard alerts
// card on every visit) call refresh() from a useEffect.
interface DashboardSummaryContextValue {
  summary: DashboardSummary | null;
  loaded: boolean;
  refresh: () => Promise<void>;
}

const DashboardSummaryContext =
  createContext<DashboardSummaryContextValue | null>(null);

type ProviderProps = { children: ReactNode };

export function DashboardSummaryProvider({ children }: ProviderProps) {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(selectAccessToken);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loaded, setLoaded] = useState(false);

  /* In-flight guard scoped to the provider instance. The provider's
   * own `accessToken` effect AND the AlertsDashboardCard's refresh-
   * on-visit effect both call `refresh()` on first dashboard mount
   * — each has its own ref guard against StrictMode double-mounts,
   * but they can't dedupe each other because they live in separate
   * components. Sharing the in-flight promise collapses the two
   * concurrent calls to a single HTTP request. Clears on settle so
   * every subsequent refresh (e.g. user reads an alert and
   * navigates back to the dashboard) still fires fresh. */
  const refreshInFlightRef = useRef<Promise<void> | null>(null);

  const refresh = useCallback(async () => {
    if (refreshInFlightRef.current) return refreshInFlightRef.current;

    const promise = (async () => {
      try {
        const response = (await dispatch(
          getAPI({ url: API_ENDPOINTS.dashboardSummary }),
        ).unwrap()) as DashboardSummary;
        setSummary(response ?? null);
      } catch (err) {
        logErrors(err);
      } finally {
        setLoaded(true);
        refreshInFlightRef.current = null;
      }
    })();

    refreshInFlightRef.current = promise;
    return promise;
  }, [dispatch]);

  // Auto-fetch the moment we have an access token (i.e. the user has
  // signed in). Without this auto-fetch the sidebar's unread-alerts
  // indicator stays blank when the user lands directly on /alerts —
  // only the dashboard card's mount-effect would otherwise trigger
  // refresh(). Gating on accessToken is required because the provider
  // mounts at the layout level (above AuthRoute), so it would otherwise
  // try to fetch on the sign-in screen with no token → 401 → loaded
  // would lock to true permanently with summary=null.
  // Ref guard absorbs React 18 StrictMode's mount→cleanup→mount in dev.
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (!accessToken) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    refresh();
  }, [accessToken, refresh]);

  const value = useMemo(
    () => ({ summary, loaded, refresh }),
    [summary, loaded, refresh],
  );

  return (
    <DashboardSummaryContext.Provider value={value}>
      {children}
    </DashboardSummaryContext.Provider>
  );
}

export function useDashboardSummary(): DashboardSummaryContextValue {
  const ctx = useContext(DashboardSummaryContext);
  if (!ctx) {
    throw new Error(
      'useDashboardSummary must be used inside <DashboardSummaryProvider>',
    );
  }
  return ctx;
}
